import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_SITE_CURRENCY,
  SUPPORTED_CURRENCIES,
  type CurrencyCode,
} from '../config/pricing';
import { getSiteCurrency } from '../utils/servicePricing';

type CurrencyContextType = {
  currency: CurrencyCode;
  setCurrency: (currency: CurrencyCode) => void;
  supportedCurrencies: readonly CurrencyCode[];
};

const SITE_CURRENCY_STORAGE_KEY = 'bluealps-site-currency';

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined,
);

type CurrencyProviderProps = {
  children: ReactNode;
};

const isCurrencyCode = (value: string): value is CurrencyCode =>
  (SUPPORTED_CURRENCIES as readonly string[]).includes(value);

const getStoredCurrency = (): CurrencyCode | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const storedCurrency = window.localStorage
    .getItem(SITE_CURRENCY_STORAGE_KEY)
    ?.toUpperCase();

  return storedCurrency && isCurrencyCode(storedCurrency)
    ? storedCurrency
    : undefined;
};

const getInitialCurrency = (): CurrencyCode =>
  getStoredCurrency() ?? getSiteCurrency(process.env) ?? DEFAULT_SITE_CURRENCY;

export const CurrencyProvider = ({ children }: CurrencyProviderProps) => {
  const [currency, setCurrency] = useState<CurrencyCode>(getInitialCurrency);

  useEffect(() => {
    window.localStorage.setItem(SITE_CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        supportedCurrencies: SUPPORTED_CURRENCIES,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);

  if (context === undefined) {
    throw new Error('useCurrency must be used inside a CurrencyProvider');
  }

  return context;
};
